import React from 'react';
import { Modal, Descriptions, Tag, Button } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { PendingItem } from '../../../services/batchApprovalApi';

// ============================================================
// Status helpers
// ============================================================

function statusTag(status: string): React.ReactNode {
  switch (status) {
    case 'S':
    case 'D': // FC120（交運單）法定送審值為 'D'
      return <Tag color="orange">待審核</Tag>;
    case 'P':
      return <Tag color="green">已核准</Tag>;
    case 'R':
      return <Tag color="red">已退回</Tag>;
    default:
      return <Tag>{status || '—'}</Tag>;
  }
}

// 27 個審核模組的前端路由映射
// 導航時帶 ?review={recordNo}，各表單讀取此參數直接載入該筆單據
function getModuleRoute(modId: string): string | null {
  const routeMap: Record<string, string> = {
    sc: '/sc',
    pwc204f1: '/c204',
    c205: '/c205',
    c212: '/c212',
    pwd201f1: '/d201',
    d202: '/d202',
    d203: '/d203',
    d204: '/d204',
    d205: '/d205',
    d206: '/d206',
    d211: '/d211',
    pwg201: '/pwg201',
    pwh201: '/pwh201',
    j201: '/j201',
    j202: '/j202',
    j203: '/j203',
    j214: '/j214',
    j215: '/j215',
    m201: '/m201',
    n202: '/n202',
    p201: '/p201',
    p203: '/p203',
    p110h: '/p110h',
    p2201: '/p2201',
    // t102（公告建檔）已自批次審核移除（2026-06-12 依使用者裁決——公告建檔即生效，無審核流）
    pw9204f1: '/pw9204f1',
    pw9221f1: '/pw9221f1',
  };
  return routeMap[modId] || null;
}

// ============================================================
// Props
// ============================================================

interface DocumentDetailModalProps {
  visible: boolean;
  item: PendingItem | null;
  onClose: () => void;
}

// ============================================================
// Component
// ============================================================

const DocumentDetailModal: React.FC<DocumentDetailModalProps> = ({
  visible,
  item,
  onClose,
}) => {
  const navigate = useNavigate();

  if (!item) return null;

  const route = getModuleRoute(item.modId);

  const handleNavigate = () => {
    if (route) {
      onClose();
      // 帶 ?review={recordNo}，各表單讀取此參數直接載入該筆單據
      navigate(`${route}?review=${encodeURIComponent(item.recordNo)}`);
    }
  };

  return (
    <Modal
      title="文件詳情"
      open={visible}
      onCancel={onClose}
      footer={[
        route && (
          <Button
            key="navigate"
            type="primary"
            icon={<LinkOutlined />}
            onClick={handleNavigate}
          >
            前往表單
          </Button>
        ),
        <Button key="close" onClick={onClose}>
          關閉
        </Button>,
      ]}
      width={480}
    >
      <Descriptions column={1} bordered size="small">
        <Descriptions.Item label="模組">
          {item.modName} ({item.modId})
        </Descriptions.Item>
        <Descriptions.Item label="單據號碼">
          {item.recordNo}
        </Descriptions.Item>
        <Descriptions.Item label="日期">
          {item.recordDate
            ? new Date(item.recordDate).toLocaleDateString('zh-TW')
            : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="建立人">
          {item.creator || '—'}
        </Descriptions.Item>
        <Descriptions.Item label="狀態">
          {statusTag(item.status)}
        </Descriptions.Item>
      </Descriptions>
    </Modal>
  );
};

export default DocumentDetailModal;
